/**
 * PaginatedList Type Definitions
 *
 * Supports both infinite scroll and classic pagination modes using
 * discriminated union types for type-safe mode handling.
 *
 * @module components/ui/PaginatedList/types
 */

import type { ReactNode } from 'react';

/**
 * Pagination mode discriminator
 * - 'infinite': Loads more items on scroll (default)
 * - 'pagination': Classic page-based navigation
 */
export type PaginatedListMode = 'infinite' | 'pagination';

/**
 * Sort configuration for sortable columns
 */
export interface SortConfig {
  /** Column key to sort by */
  readonly key: string;
  /** Sort direction */
  readonly direction: 'asc' | 'desc';
}

/**
 * Gap size options for spacing between list items
 * 'none' is used internally for card mode where rows have border separators
 */
export type PaginatedListGap = 'none' | 'xs' | 'sm' | 'md' | 'lg';

/**
 * Column header configuration for list display
 * Used with showColumnHeaders prop to render table-like headers
 */
export interface ColumnHeaderConfig {
  /** Unique key for the column */
  readonly key: string;

  /** Label text or i18n key for translation */
  readonly label: string;

  /** Column width (CSS value like '100px', '1fr', or percentage) */
  readonly width?: string;

  /** Text alignment within the column */
  readonly align?: 'left' | 'center' | 'right';

  /** Whether to hide this column on mobile breakpoint */
  readonly hideOnMobile?: boolean;

  /** Sort key for this column (if sortable). Omit to disable sorting for this column. */
  readonly sortKey?: string;
}

/**
 * Card mode configuration for table-like list display.
 * Groups all props related to card wrapper and column headers.
 */
export interface CardConfig {
  /** Enable card container wrapping */
  readonly enabled: boolean;
  /** Card header content */
  readonly header?: ReactNode;
  /** Column headers configuration */
  readonly columns?: {
    /** Whether to show column headers (desktop only) */
    readonly show: boolean;
    /** Column header configurations */
    readonly config: readonly ColumnHeaderConfig[];
  };
  /** Filter slot for integrated filters */
  readonly filterSlot?: ReactNode;
  /** Sort configuration */
  readonly sort?: {
    /** Current sort state */
    readonly config: SortConfig;
    /** Callback when sort changes */
    readonly onChange: (config: SortConfig) => void;
  };
}

/**
 * Custom state renderers for empty, error, and loading states.
 */
export interface StateRenderers {
  /** Custom empty state renderer */
  readonly empty?: () => ReactNode;
  /** Custom error state renderer */
  readonly error?: (errorMessage: string) => ReactNode;
  /** i18n key for empty state message @default 'common.noItems' */
  readonly emptyMessage?: string;
}

/**
 * Behavior configuration for list interactions.
 * @template T - Type of items in the list
 */
export interface BehaviorConfig<T> {
  /** Enable keyboard navigation @default true */
  readonly enableKeyboardNavigation?: boolean;
  /** Item selection callback (Enter/Space) */
  readonly onItemSelect?: (item: T, index: number) => void;
  /** Item click callback */
  readonly onItemClick?: (item: T, index: number) => void;
}

/**
 * Styling configuration for list appearance.
 */
export interface StylingConfig {
  /** Gap between list items @default 'sm' */
  readonly gap?: PaginatedListGap;
  /** Minimum height for container */
  readonly minHeight?: number | string;
  /** Custom className */
  readonly className?: string;
  /** Skeleton count during loading @default 3 */
  readonly skeletonCount?: number;
}

/**
 * Base props shared across all pagination modes
 * @template T - Type of items in the list
 */
export interface PaginatedListBaseProps<T> {
  /**
   * Data items to render
   * @remarks Renamed from 'items' for consistency with React Query patterns
   */
  data: T[];

  /**
   * Render function for each item
   * @param item - The item to render
   * @param index - Index of the item in the list
   * @param isFocused - Whether the item is focused via keyboard navigation
   * @returns ReactNode to render for this item
   */
  renderItem: (item: T, index: number, isFocused: boolean) => ReactNode;

  /**
   * Key extractor for stable item identification
   * @param item - The item to extract key from
   * @param index - Index of the item
   * @returns Unique string key for the item
   */
  keyExtractor: (item: T, index: number) => string;

  /** Whether the list is currently loading data */
  isLoading: boolean;

  /**
   * Whether the list has completed at least one fetch attempt.
   * When `false`, the empty branch is suppressed in favor of skeletons —
   * prevents the empty state flashing before the first request resolves.
   * Defaults to `true` so legacy callers keep their previous behavior.
   */
  hasLoaded?: boolean;

  /** Error message to display (triggers error state when set) */
  error?: string | null;

  /** ARIA label for the list container for accessibility */
  'aria-label'?: string;

  // =========================================================================
  // GROUPED CONFIGURATION OBJECTS (new API)
  // =========================================================================

  /**
   * Card mode configuration for table-like display.
   * Groups: withCard, cardHeader, showColumnHeaders, columnHeaders,
   *         filterSlot, sortConfig, onSortChange
   */
  cardConfig?: CardConfig;

  /**
   * Custom state renderers configuration.
   * Groups: renderEmpty, renderError, emptyMessage
   */
  stateRenderers?: StateRenderers;

  /**
   * Behavior configuration for interactions.
   * Groups: enableKeyboardNavigation, onItemSelect, onItemClick
   */
  behavior?: BehaviorConfig<T>;

  /**
   * Styling configuration for appearance.
   * Groups: gap, minHeight, className, skeletonCount
   */
  styling?: StylingConfig;
}

/**
 * Props specific to infinite scroll mode
 * @template T - Type of items in the list
 */
export interface InfiniteScrollProps<T> extends PaginatedListBaseProps<T> {
  /**
   * Pagination mode
   * @default 'infinite'
   */
  mode?: 'infinite';

  /** Whether there are more items available to load */
  hasNextPage: boolean;

  /** Callback to trigger loading of next page */
  onLoadMore: () => void;

  /**
   * Show explicit "Load More" button instead of auto-loading on scroll
   * @default false
   */
  showLoadMoreButton?: boolean;

  /**
   * Intersection observer threshold for triggering load more (0-1)
   * @default 0.1
   */
  loadMoreThreshold?: number;
}

/**
 * Props specific to classic pagination mode
 * @template T - Type of items in the list
 */
export interface PaginationProps<T> extends PaginatedListBaseProps<T> {
  /** Pagination mode - must be 'pagination' for this variant */
  mode: 'pagination';

  /** Current active page (1-indexed) */
  currentPage: number;

  /** Total number of pages */
  totalPages: number;

  /** Total number of items across all pages */
  totalItems?: number;

  /** Number of items per page */
  pageSize?: number;

  /**
   * Callback when page changes
   * @param page - New page number (1-indexed)
   */
  onPageChange: (page: number) => void;

  /**
   * Number of sibling pages to show on each side of current page
   * @default 1
   */
  siblings?: number;

  /**
   * Number of pages to show at boundaries (start and end)
   * @default 1
   */
  boundaries?: number;
}

/**
 * Combined props type using discriminated union
 * Enables type-safe handling of mode-specific props
 * @template T - Type of items in the list
 */
export type PaginatedListProps<T> = InfiniteScrollProps<T> | PaginationProps<T>;

/**
 * Type guard for infinite scroll mode
 * @param props - Props to check
 * @returns True if props are for infinite scroll mode
 *
 * @example
 * ```typescript
 * function PaginatedList<T>(props: PaginatedListProps<T>) {
 *   if (isInfiniteMode(props)) {
 *     // TypeScript knows props has hasNextPage, onLoadMore, etc.
 *     console.log(props.hasNextPage);
 *   }
 * }
 * ```
 */
export function isInfiniteMode<T>(
  props: PaginatedListProps<T>
): props is InfiniteScrollProps<T> {
  return props.mode !== 'pagination';
}

/**
 * Type guard for pagination mode
 * @param props - Props to check
 * @returns True if props are for pagination mode
 *
 * @example
 * ```typescript
 * function PaginatedList<T>(props: PaginatedListProps<T>) {
 *   if (isPaginationMode(props)) {
 *     // TypeScript knows props has currentPage, totalPages, etc.
 *     console.log(props.currentPage);
 *   }
 * }
 * ```
 */
export function isPaginationMode<T>(
  props: PaginatedListProps<T>
): props is PaginationProps<T> {
  return props.mode === 'pagination';
}
